import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import Api from "api";

export default function HealthPlanMultiSelect({
  open,
  selectedHealthPlans,
  onChange,
  disabled = false,
}) {
  const [healthPlans, setHealthPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const dropdownStyles = {
    PaperProps: {
      style: {
        maxHeight: 200,
        overflowY: "auto",
      },
    },
  };

  useEffect(() => {
    if (open) {
      const fetchHealthPlans = async () => {
        try {
          const response = await Api.healthPlanList.listHealthPlans();

          if (response.status === 200 && response.data.length > 0) {
            setHealthPlans(response.data); // Set the health plans data
          } else if (response.data.length < 1) {
            console.error("API returned an empty list of health plans.");
          }
        } catch (error) {
          console.error("Could not fetch health plans", error);
        } finally {
          setLoading(false); // Set loading to false after data is fetched
        }
      };

      fetchHealthPlans();
    }
  }, [open]);

  const allHealthPlanKeys = healthPlans
    .filter((plan) => plan.healthPlanName !== "AHCS") // Exclude AHCS from "Select All"
    .map((plan) => plan.healthPlanKey);

  // Handler for when the selected health plans change
  const handleSelectChange = (event) => {
    if (disabled) return;
    const value = event.target.value;

    // If "Select All" is clicked, toggle selection
    if (value.includes("all")) {
      if (selectedHealthPlans.length === allHealthPlanKeys.length) {
        onChange([]); // Deselect all if everything was selected
      } else {
        onChange(allHealthPlanKeys); // Select all (excluding disabled ones)
      }
    } else {
      onChange(value); // Update the parent component with selected health plans
    }
  };

  const isSelectAllChecked =
    selectedHealthPlans.length === allHealthPlanKeys.length;

  return (
    <FormControl fullWidth size="small">
      <InputLabel>Health Plans</InputLabel>
      {loading ? (
        <CircularProgress />
      ) : (
        <Select
          multiple
          value={selectedHealthPlans}
          label="Health Plan"
          onChange={handleSelectChange}
          disabled={disabled}
          renderValue={(selected) =>
            healthPlans
              .filter((plan) => selected.indexOf(plan.healthPlanKey) > -1)
              .map((plan) => plan.healthPlanDisplayName)
              .join(", ")
          }
          MenuProps={dropdownStyles}
        >
          {/* "Select All" option */}
          <MenuItem value="all">
            <Checkbox checked={isSelectAllChecked} />
            <ListItemText primary="Select All" />
          </MenuItem>

          {healthPlans.map((plan) => (
            <MenuItem
              key={plan.healthPlanKey}
              value={plan.healthPlanKey}
              disabled={plan.healthPlanName === "AHCS"}
            >
              <Checkbox
                sx={{ padding: 0 }}
                checked={selectedHealthPlans.indexOf(plan.healthPlanKey) > -1}
              />
              <ListItemText
                primary={`${plan.healthPlanDisplayName} (${plan.healthPlanName})`}
              />
            </MenuItem>
          ))}
        </Select>
      )}
    </FormControl>
  );
}
